import { Alert, Box, Button, Divider, Menu, MenuItem, TextField } from '@mui/material'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useOwnedCollections, usePinToCollection } from '~/api/hooks/useCollections'
import { getInlineMessage } from '~/errors/handleApiError'
import { ROUTES } from '~/constants/routes'
import type { PinV1 } from '~/pin/PinV1'
import { DIALOGS } from '~/components/Charts/chrome/chartChrome'

export function PinButton({ pin }: { pin: PinV1 }) {
  const collections = useOwnedCollections()
  const pinToCollection = usePinToCollection()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [titleInput, setTitleInput] = useState('')
  const inlineMsg = pinToCollection.error ? getInlineMessage(pinToCollection.error) : null

  const closeMenu = () => {
    setAnchor(null)
    setTitleInput('')
    pinToCollection.reset()
  }

  return (
    <>
      <Button size="small" onClick={(e) => setAnchor(e.currentTarget)}>
        {DIALOGS.pinToCollection}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={closeMenu} disableAutoFocusItem>
        {pinToCollection.error ? (
          <Alert severity="warning" sx={{ mx: 1, my: 0.5 }}>
            {inlineMsg ?? DIALOGS.pinFailed}
          </Alert>
        ) : null}
        <Box sx={{ px: 2, py: 1, minWidth: 240 }}>
          {/* eslint-disable jsx-a11y/no-autofocus -- Menu TextField is the primary affordance; disableAutoFocusItem on Menu ensures this is the only focused element */}
          <TextField
            label={DIALOGS.pinNameOptional}
            size="small"
            autoFocus
            fullWidth
            value={titleInput}
            onChange={(e) => setTitleInput(e.target.value)}
            inputProps={{ maxLength: 80 }}
          />
          {/* eslint-enable jsx-a11y/no-autofocus */}
        </Box>
        <Divider />
        {collections.isLoading ? <MenuItem disabled>{DIALOGS.loading}</MenuItem> : null}
        {collections.isError ? (
          <MenuItem disabled>{DIALOGS.couldNotLoadCollections}</MenuItem>
        ) : null}
        {!collections.isLoading && !collections.isError && (collections.data ?? []).length === 0 ? (
          <MenuItem component={Link} to={ROUTES.collections}>
            {DIALOGS.createNewCollection}
          </MenuItem>
        ) : null}
        {(collections.data ?? []).map((c) => (
          <MenuItem
            key={c.id}
            disabled={pinToCollection.isPending}
            onClick={async () => {
              const trimmed = titleInput.trim()
              const pinToSend: PinV1 = trimmed
                ? { ...pin, render_hint: { ...pin.render_hint, title: trimmed } }
                : pin
              try {
                await pinToCollection.mutateAsync({ collectionId: c.id, pin: pinToSend })
                closeMenu()
              } catch {
                // Menu stays open; pinToCollection.error renders as <Alert> at the top of the menu.
              }
            }}
          >
            {`${c.is_dashboard ? '📌 ' : ''}${c.name}`}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
